import { Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, debounceTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ContainerListItem, PurchaseShipmentService } from '../../../_services/purchase-shipment.service';
import { AlertService } from '../../../_services/alert.service';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';
import { Listening, listenOnce, speechSupported } from '../../../../Shared/_services/speech';
import { shipmentStatusClass, shipmentStatusDot, shipmentStatusText } from './shipment-status';
import { findState } from './container-command';
import { readContainerLinesSheet, writeContainerLinesSheet } from './container-lines-sheet';
import { ContainerLinesImportPreviewComponent } from './container-lines-import-preview.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// The Shipments page's Containers view: every line in every container as a
// wide table (PO, product, incoterm, quantity, price, subtotal, invoice,
// container, BL, dates, ports, vessel, line, state) - searchable across all
// of it, tabs by the container's state, and the same mic / state menu,
// which changes the line's container.
@Component({
    selector: 'app-containers-list',
    imports: [CommonModule, DatePipe, FormsModule, RouterLink, MatIconModule, MatTooltipModule, MatMenuModule, MatPaginatorModule, MatProgressSpinnerModule, PaginatorPagesDirective],
    templateUrl: './containers-list.component.html',
    styleUrls: ['../purchases.component.css', './shipments.component.css']
})
export class ContainersListComponent implements OnInit {
  private shipmentService = inject(PurchaseShipmentService);
  private alertService = inject(AlertService);
  private router = inject(Router);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);
  private search$ = new Subject<void>();
  private dialog = inject(MatDialog);

  readonly tabs = ['ALL', 'BOOKED', 'IN TRANSIT', 'ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED', 'CANCELLED'];
  readonly stateOptions = [
    { value: 'BOOKED', label: 'Booked' },
    { value: 'IN TRANSIT', label: 'In transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'CLEARED', label: 'Cleared' },
    { value: 'DELIVERED', label: 'Delivered' },
  ];
  readonly getStatusClass = shipmentStatusClass;
  readonly skeletonRows = [1, 2, 3, 4, 5];

  containers: ContainerListItem[] = [];
  statusCounts: Record<string, number> = {};
  count = 0;
  totals: { currency: string; total: number }[] = [];
  units = 0;
  loading = true;
  search = '';
  status = 'ALL';
  pageIndex = 0;
  pageSize = 20;

  listeningId: number | null = null;
  savingId: number | null = null;
  private listening: Listening | null = null;

  ngOnInit(): void {
    this.search$.pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.pageIndex = 0;
      this.load();
    });
    this.load();
  }

  load(): void {
    this.loading = true;
    this.shipmentService
      .listContainers({ search: this.search.trim() || undefined, status: this.status, pageIndex: this.pageIndex, pageSize: this.pageSize })
      .subscribe({
        next: (r) => {
          this.containers = r.items;
          this.count = r.count;
          this.statusCounts = r.statusCounts ?? {};
          this.totals = r.totals ?? [];
          this.units = r.units ?? 0;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.alertService.alert(error?.error?.message || 'Could not load containers', 'error');
        },
      });
  }

  onSearch(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.search = '';
    this.pageIndex = 0;
    this.load();
  }

  setTab(tab: string): void {
    this.status = tab;
    this.pageIndex = 0;
    this.load();
  }

  tabCount(tab: string): number {
    if (tab === 'ALL') return Object.values(this.statusCounts).reduce((a, b) => a + b, 0);
    return this.statusCounts[tab] ?? 0;
  }

  onPage(event: PageEvent): void {
    if (event.pageSize !== this.pageSize) {
      this.pageSize = event.pageSize;
      this.pageIndex = 0;
    } else {
      this.pageIndex = event.pageIndex;
    }
    this.load();
  }

  // A line opens its container's page (or its shipment, if it has no container).
  open(c: ContainerListItem): void {
    if (c.containerId) this.router.navigate(['/shipments/container', c.containerId]);
    else this.router.navigate(['/shipments/edit', c.shipmentId]);
  }

  currencySymbol(code: string): string {
    return ({ GBP: '£', EUR: '€', USD: '$', INR: '₹' } as Record<string, string>)[code] ?? code + ' ';
  }

  statusDot(status: string): string {
    return shipmentStatusDot(status);
  }

  // Days to the shipment's ETA while still on its way (negative = late).
  daysToEta(c: ContainerListItem): number | null {
    if (!c.eta || !['BOOKED', 'IN TRANSIT'].includes(c.status)) return null;
    const eta = new Date(c.eta);
    const today = new Date();
    return Math.round((Date.UTC(eta.getFullYear(), eta.getMonth(), eta.getDate()) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86_400_000);
  }

  // ---- Selection (kept across pages, searches and tabs) ----
  readonly selected = new Map<number, ContainerListItem>();
  bulkBusy = false;

  isSelected(c: ContainerListItem): boolean {
    return this.selected.has(c.id);
  }

  toggleSelect(c: ContainerListItem, event?: Event): void {
    event?.stopPropagation();
    if (this.selected.has(c.id)) this.selected.delete(c.id);
    else this.selected.set(c.id, c);
  }

  get allOnPageSelected(): boolean {
    return this.containers.length > 0 && this.containers.every((c) => this.selected.has(c.id));
  }

  get someOnPageSelected(): boolean {
    return this.containers.some((c) => this.selected.has(c.id)) && !this.allOnPageSelected;
  }

  toggleSelectPage(): void {
    if (this.allOnPageSelected) this.containers.forEach((c) => this.selected.delete(c.id));
    else this.containers.forEach((c) => this.selected.set(c.id, c));
  }

  clearSelection(): void {
    this.selected.clear();
  }

  // The selected lines' containers that can still change state.
  get selectedContainerIds(): number[] {
    return [...new Set([...this.selected.values()].filter((l) => l.containerId && !['RECEIVED', 'CANCELLED'].includes(l.status)).map((l) => l.containerId as number))];
  }

  // Straight from what's selected - same file as Export, so it imports back.
  exportSelected(): void {
    const lines = [...this.selected.values()];
    writeContainerLinesSheet(lines, `container-lines-selected-${new Date().toISOString().slice(0, 10)}.xlsx`);
    this.alertService.alert(`${lines.length} line${lines.length === 1 ? '' : 's'} exported`, 'success');
  }

  bulkSetState(value: string): void {
    const ids = this.selectedContainerIds;
    if (!ids.length) {
      this.alertService.alert('None of the selected lines are in a container that can change (received ones stay as they are)', 'error');
      return;
    }
    const label = this.stateOptions.find((o) => o.value === value)?.label ?? value;
    this.bulkBusy = true;
    this.shipmentService.setContainerStatus(ids, value).subscribe({
      next: (r) => {
        this.bulkBusy = false;
        this.alertService.alert(`${r.updated} container${r.updated === 1 ? '' : 's'} set to ${label}` + (r.skipped ? ` - ${r.skipped} skipped` : ''), 'success');
        this.selected.clear();
        this.load();
      },
      error: (error) => {
        this.bulkBusy = false;
        this.alertService.alert(error?.error?.message || 'Could not update the containers', 'error');
      },
    });
  }

  // ---- Spreadsheet: the table's columns, out and back in ----
  exporting = false;
  importing = false;

  // Every line matching the search and tab (all pages).
  exportSheet(): void {
    this.exporting = true;
    this.shipmentService
      .listContainers({ search: this.search.trim() || undefined, status: this.status, pageIndex: 0, pageSize: 100, all: true })
      .subscribe({
        next: (r) => {
          this.exporting = false;
          if (!r.items.length) {
            this.alertService.alert('No lines to export with this search', 'error');
            return;
          }
          writeContainerLinesSheet(r.items, `container-lines-${new Date().toISOString().slice(0, 10)}.xlsx`);
          this.alertService.alert(`${r.items.length} line${r.items.length === 1 ? '' : 's'} exported`, 'success');
        },
        error: (error) => {
          this.exporting = false;
          this.alertService.alert(error?.error?.message || 'Export failed', 'error');
        },
      });
  }

  // Reads the file, has the server check it, then shows every change.
  async onImportFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.importing = true;
    try {
      const rows = await readContainerLinesSheet(file);
      if (!rows.length) {
        this.importing = false;
        this.alertService.alert('No lines found in that file', 'error');
        return;
      }
      this.shipmentService.importContainerLines({ dryRun: true, rows }).subscribe({
        next: (result) => {
          this.importing = false;
          this.dialog
            .open(ContainerLinesImportPreviewComponent, { width: '820px', maxWidth: '96vw', data: { fileName: file.name, rows, result } })
            .afterClosed()
            .subscribe((applied) => {
              if (applied) this.load();
            });
        },
        error: (error) => {
          this.importing = false;
          this.alertService.alert(error?.error?.message || 'Could not check that file', 'error');
        },
      });
    } catch {
      this.importing = false;
      this.alertService.alert("Couldn't read that file - use an .xlsx exported from here", 'error');
    }
  }

  // ---- State: said into the row's mic, or picked from its badge ----
  speak(c: ContainerListItem): void {
    if (!c.containerId) return;
    if (this.listeningId === c.id) {
      this.listening?.stop();
      return;
    }
    if (!speechSupported()) {
      this.alertService.alert("This browser can't listen (Brave blocks it) - pick the state from its badge instead", 'error');
      return;
    }
    this.listening?.stop();
    this.listeningId = c.id;
    this.listening = listenOnce(this.zone);
    this.listening.result
      .then((heard) => {
        this.listeningId = null;
        const state = findState(heard);
        if (!state) return this.alertService.alert(`Heard “${heard.split(' | ')[0]}” - say a state, e.g. cleared, delivered, at sea`, 'error');
        if (state.value === 'RECEIVED') return this.alertService.alert('Received is set by checking the container in at Incoming Stocks', 'error');
        if (state.value === c.status) return this.alertService.alert(`${c.containerNo || 'It'} is already ${state.label.toLowerCase()}`, 'success');
        this.setState(c, state.value);
      })
      .catch((message: string) => {
        this.listeningId = null;
        this.alertService.alert(message, 'error');
      });
  }

  setState(c: ContainerListItem, value: string): void {
    const label = this.stateOptions.find((o) => o.value === value)?.label ?? value;
    if (!c.containerId) return;
    this.savingId = c.id;
    this.shipmentService.setContainerStatus([c.containerId], value).subscribe({
      next: () => {
        this.savingId = null;
        this.alertService.alert(`${c.containerNo || 'Container'} set to ${label}`, 'success');
        this.load();
      },
      error: (error) => {
        this.savingId = null;
        this.alertService.alert(error?.error?.message || 'Could not update the container', 'error');
      },
    });
  }
}
